import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import Loading from '../components/Loading';
import { getAuthData } from '../utils/auth';

// Lazy load pages for better performance
const Auth = lazy(() => import('../pages/auth/Auth'));
const ResetPasswordPage = lazy(() => import('../pages/resetPassword/ResetPassword'));
const SignUpPage = lazy(() => import('../pages/auth/SignUp'));
import UserRoutes from './userRoute/UserRoute';
import AdminRoutes from './adminRoute/AdminRoute';

const getRoutes = () => {
  const { accessToken, role } = getAuthData();
  const userRole = localStorage.getItem('role');

  // Helper function to handle redirects for authenticated users
  const getPublicRouteElement = (element: any, allowSuccessState = false, isLoginPage = false) => {
    if (!accessToken) {
      return <Suspense fallback={<Loading />}>{element}</Suspense>;
    }

    // Allow signup success page to be shown even when authenticated
    if (allowSuccessState && window.location.search.includes('success=true')) {
      return <Suspense fallback={<Loading />}>{element}</Suspense>;
    }

    // Allow access to login pages to handle role validation
    if (isLoginPage) {
      return <Suspense fallback={<Loading />}>{element}</Suspense>;
    }

    return <Navigate to={role === 'admin' ? '/dashboard' : '/user/dashboard'} replace />;
  };

  // Public routes that don't require authentication
  const PublicRoutes: RouteObject[] = [
    {
      path: '/admin/login',
      element: getPublicRouteElement(<Auth isAdminLogin={true} />, false, true),
    },
    {
      path: '/admin/forgot-password',
      element: getPublicRouteElement(<Auth isAdminLogin={true} />, false, true),
    },
    {
      path: '/user/login',
      element: getPublicRouteElement(<Auth isAdminLogin={false} />, false, true),
    },
    {
      path: '/user/forgot-password',
      element: getPublicRouteElement(<Auth isAdminLogin={false} />, false, true),
    },
    {
      path: '/reset-password',
      element: (
        <Suspense fallback={<Loading />}>
          <ResetPasswordPage />
        </Suspense>
      ),
    },
    {
      path: '/admin/reset-password',
      element: (
        <Suspense fallback={<Loading />}>
          <ResetPasswordPage />{' '}
        </Suspense>
      ),
    },
    {
      path: '/user/signup',
      element: getPublicRouteElement(<SignUpPage />, true),
    },
  ];

  const routes: RouteObject[] = [
    ...PublicRoutes,
    ...AdminRoutes,
    ...UserRoutes,
    {
      path: '/',
      element: (
        <Navigate
          to={
            accessToken
              ? role === 'admin'
                ? '/dashboard'
                : '/user/dashboard'
              : userRole === 'admin'
                ? '/admin/login'
                : '/user/login'
          }
          replace
        />
      ),
    },
    {
      path: '*',
      element: (
        <Navigate
          to={
            accessToken
              ? role === 'admin'
                ? '/dashboard'
                : '/user/dashboard'
              : userRole === 'admin'
                ? '/admin/login'
                : '/user/login'
          }
          replace
        />
      ),
    },
  ];

  return routes;
};

// Create and export the router
export const router = createBrowserRouter(getRoutes());
